import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createEmbedHost } from '@Sage/ssm-embed/host';

import { getAllowedOriginFromSrc } from '../protocol/embedProtocol';
import { canPostMessageToWindow } from './canPostMessageToWindow';

export function useIframeEmbedBridge({
    allowedOrigin: allowedOriginProp,
    childCancelTimeoutMs,
    getAuthContext,
    handshakeTimeoutMs = 0,
    iframeRef,
    iframeSrc,
    initContext,
    initExtras,
    isActive,
    maxIframeHeight,
    mode,
    onCloseFromEmbed,
    onEmbedReady,
    onHandshakeTimeout,
    onInitError,
    onNotify,
    onStateChange,
}) {
    const allowedOrigin = useMemo(() => {
        if (allowedOriginProp) return allowedOriginProp;
        return getAllowedOriginFromSrc(iframeSrc) ?? '';
    }, [allowedOriginProp, iframeSrc]);

    const [iframePixelHeight, setIframePixelHeight] = useState(null);
    const [iframeState, setIframeState] = useState(null);
    const [isHandshakeReady, setIsHandshakeReady] = useState(false);
    const hostRef = useRef(null);
    const optionsRef = useRef(null);

    optionsRef.current = {
        allowedOrigin,
        childCancelTimeoutMs,
        getAuthContext,
        handshakeTimeoutMs,
        iframeRef,
        iframeSrc,
        initContext,
        initExtras,
        maxIframeHeight,
        mode,
        onClose: onCloseFromEmbed,
        onEmbedReady,
        onHandshakeReady: setIsHandshakeReady,
        onHandshakeTimeout,
        onInitError,
        onNotify,
        onResize: setIframePixelHeight,
        onState: setIframeState,
        onStateChange,
    };

    useEffect(() => {
        if (!isActive) {
            hostRef.current?.stop();
            hostRef.current = null;
            return undefined;
        }

        const host = createEmbedHost(() => {
            const options = optionsRef.current;

            return {
                ...options,
                isInboundTrusted: ({ origin, source }) => {
                    if (!options.allowedOrigin || origin !== options.allowedOrigin) return false;
                    if (
                        options.iframeRef.current &&
                        source !== options.iframeRef.current.contentWindow
                    ) {
                        return false;
                    }
                    return true;
                },
                sendEnvelope: (envelope) => {
                    const contentWindow = options.iframeRef.current?.contentWindow;
                    if (!canPostMessageToWindow(contentWindow, options.allowedOrigin)) {
                        return false;
                    }
                    try {
                        contentWindow.postMessage(envelope, options.allowedOrigin);
                    } catch {
                        return false;
                    }
                    return true;
                },
                subscribe: (handler) => {
                    const listener = (event) => {
                        handler(event.data, { origin: event.origin, source: event.source });
                    };
                    window.addEventListener('message', listener);
                    return () => window.removeEventListener('message', listener);
                },
            };
        });

        host.start();
        hostRef.current = host;

        return () => {
            host.stop();
            if (hostRef.current === host) {
                hostRef.current = null;
            }
        };
    }, [isActive]);

    useEffect(() => {
        hostRef.current?.syncHandshakeTimer();
    }, [handshakeTimeoutMs, iframeSrc, isActive, isHandshakeReady]);

    const sendActionToIframe = useCallback((action, payload = {}) => {
        return hostRef.current?.sendAction(action, payload) ?? false;
    }, []);

    const requestChildCancel = useCallback((payload) => {
        return hostRef.current?.requestChildCancel(payload) ?? false;
    }, []);

    const resetBridge = useCallback(() => {
        hostRef.current?.reset();
    }, []);

    const sendInit = useCallback((request) => {
        return hostRef.current?.sendInit(request);
    }, []);

    return {
        allowedOrigin,
        iframePixelHeight,
        iframeState,
        isHandshakeReady,
        requestChildCancel,
        resetBridge,
        sendActionToIframe,
        sendInit,
    };
}
